import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { BUSINESS_TYPES, tierById } from '@/lib/sponsor/catalog';
import { registerAccount, useSponsorStore } from '@/lib/sponsor/store';
import Field, { inputClass } from '../components/Field';
import { useSponsorContext } from '../context';

type Form = { email: string; phone: string; company: string; businessType: string; licence: string; licencePhoto: string; agree: boolean; otp: string };
type Errors = Partial<Record<keyof Form, string>>;

const validators: { [K in keyof Form]?: (v: Form[K]) => string | undefined } = {
  email: (v) => (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim()) ? undefined : 'Enter an email like name@company.com.'),
  phone: (v) => (v.replace(/\D/g, '').length >= 8 ? undefined : 'Enter a phone number with at least 8 digits.'),
  company: (v) => (v.trim().length > 1 ? undefined : 'Enter your company name.'),
  licence: (v) => (v.trim().length > 3 ? undefined : 'Enter your business licence number.'),
  licencePhoto: (v) => (v ? undefined : 'Add a photo of your licence.'),
  agree: (v) => (v ? undefined : 'Tick the box to agree to the ad rules.'),
};

export default function SponsorRegisterPage() {
  const store = useSponsorStore();
  const { selection } = useSponsorContext();
  const navigate = useNavigate();
  const [form, setForm] = useState<Form>({ email: '', phone: '', company: '', businessType: BUSINESS_TYPES[0], licence: '', licencePhoto: '', agree: false, otp: '' });
  const [errors, setErrors] = useState<Errors>({});
  const [sentCode, setSentCode] = useState<string | null>(null);

  if (store.currentAccountId) return <Navigate to="/sponsor/checkout" replace />;

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const validate = (): Errors => {
    const e: Errors = {};
    (Object.keys(validators) as (keyof Form)[]).forEach((k) => {
      const fn = validators[k] as ((v: unknown) => string | undefined) | undefined;
      const msg = fn?.(form[k]);
      if (msg) e[k] = msg;
    });
    return e;
  };

  const onSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    const e = validate();
    if (sentCode && form.otp.trim() !== sentCode) e.otp = 'Enter the 6-digit code we sent to your phone.';
    setErrors(e);
    if (Object.keys(e).length) {
      document.getElementById(`reg-${Object.keys(e)[0]}`)?.focus();
      return;
    }
    if (!sentCode) {
      // Simulated SMS: the real API sends the code to the phone.
      setSentCode(String(Math.floor(100000 + Math.random() * 900000)));
      return;
    }
    registerAccount({
      company: form.company.trim(), email: form.email.trim(), phone: form.phone.trim(),
      businessType: form.businessType, licence: form.licence.trim(), licencePhoto: form.licencePhoto,
    });
    navigate('/sponsor/checkout');
  };

  const t = tierById(selection.tier);
  const aria = (k: keyof Form) => ({ 'aria-invalid': !!errors[k], 'aria-describedby': errors[k] ? `reg-${k}-err` : undefined });

  return (
    <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr] items-start">
      <form noValidate onSubmit={onSubmit} className="bg-background-50 border border-background-200 rounded-2xl p-5 md:p-6 flex flex-col gap-4">
        <button type="button" onClick={() => navigate('/sponsor')} className="self-start text-sm text-foreground-500 hover:text-foreground-950 cursor-pointer">‹ Back to packages</button>
        <h1 className="font-heading font-bold text-xl">Create your sponsor account</h1>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="reg-email" label="Work email" error={errors.email}>
            <input id="reg-email" type="email" autoComplete="email" className={inputClass} value={form.email} onChange={(e) => set('email', e.target.value)} {...aria('email')} />
          </Field>
          <Field id="reg-phone" label="Phone" error={errors.phone}>
            <input id="reg-phone" type="tel" autoComplete="tel" className={inputClass} value={form.phone} onChange={(e) => set('phone', e.target.value)} {...aria('phone')} />
          </Field>
          <Field id="reg-company" label="Company name" error={errors.company}>
            <input id="reg-company" autoComplete="organization" className={inputClass} value={form.company} onChange={(e) => set('company', e.target.value)} {...aria('company')} />
          </Field>
          <Field id="reg-businessType" label="Business type">
            <select id="reg-businessType" className={inputClass} value={form.businessType} onChange={(e) => set('businessType', e.target.value)}>
              {BUSINESS_TYPES.map((b) => <option key={b}>{b}</option>)}
            </select>
          </Field>
          <Field id="reg-licence" label="Licence number (MOC)" error={errors.licence}>
            <input id="reg-licence" className={inputClass} value={form.licence} onChange={(e) => set('licence', e.target.value)} {...aria('licence')} />
          </Field>
          <Field id="reg-licencePhoto" label="Photo of licence" error={errors.licencePhoto}>
            <input id="reg-licencePhoto" type="file" accept="image/*,application/pdf" className={`${inputClass} py-2`} onChange={(e) => set('licencePhoto', e.target.files?.[0]?.name ?? '')} {...aria('licencePhoto')} />
          </Field>
        </div>
        <div className="flex flex-col gap-1">
          <label className="flex gap-2 items-start text-sm text-foreground-600">
            <input id="reg-agree" type="checkbox" className="mt-1 accent-primary-500" checked={form.agree} onChange={(e) => set('agree', e.target.checked)} {...aria('agree')} />
            I agree to the ad rules: no alcohol, tobacco, gambling or politics. OPS checks every ad before it goes live.
          </label>
          {errors.agree && <span id="reg-agree-err" className="text-xs text-secondary-700">{errors.agree}</span>}
        </div>

        {sentCode && (
          <div className="rounded-xl bg-primary-50 border border-primary-200 p-4 flex flex-col gap-3">
            <p className="text-sm">We sent a 6-digit code to {form.phone}. <span className="text-foreground-500">(Demo: the code is <b>{sentCode}</b>.)</span></p>
            <Field id="reg-otp" label="Code from SMS" error={errors.otp}>
              <input id="reg-otp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} className={`${inputClass} max-w-40 tracking-widest`} value={form.otp} onChange={(e) => set('otp', e.target.value)} {...aria('otp')} />
            </Field>
          </div>
        )}

        <button type="submit" className="w-full sm:w-auto sm:self-start px-6 py-3.5 rounded-full bg-foreground-950 text-background-50 font-bold cursor-pointer hover:bg-foreground-800">
          {sentCode ? 'Create account' : 'Send SMS code'}
        </button>
      </form>

      <aside className="bg-background-50 border border-background-200 rounded-2xl p-5 flex flex-col gap-3">
        <h2 className="font-heading font-bold">What happens next</h2>
        <ol className="flex flex-col gap-2 text-sm text-foreground-600 list-decimal pl-5">
          <li>Pick and pay for {t.name}</li>
          <li>OPS checks your licence and ad</li>
          <li>Ads go live, dashboard fills up</li>
        </ol>
        <p className="text-sm text-foreground-500">You can pay now. OPS checks your licence together with your ad, within 1 working day.</p>
      </aside>
    </div>
  );
}
